import cn from 'classnames'
import type { Category } from '@mc/api'
import { useTranslation } from '@mc/i18n'
import { Locations } from '@mc/track-event'
import { useHPHeadingsExp } from 'experiments/pen-4680_v1_hp_title_subtitle'
import { useQuizOutcomeFramingExp } from 'experiments/pen-5819_v1_quiz_outcome_framing'
import {
  type Motivation,
  useCategoryNameToIdMap,
  useMotivations,
  useMotivationsCopyMap,
  useMotivationToCategoriesMap,
} from 'pages/find-my-classes/hooks/useCategorySelections'
import { trackCheckboxToggled, trackPrimerAnswerSubmitted } from '../../events'
import { CheckboxSelectionTight } from '../CheckboxSelection'
import { TooltipCta } from '../TooltipCta'
import { useWhatBringsYouVariantPALM } from './useWhatBringsYouVariantPALM'

export const WhatBringsYouVariantPALM = () => {
  const { t } = useTranslation('@mc/next')
  const {
    sel,
    setSel,
    toggle,
    isSelected,
    selectedOptions,
    ctaHref,
    trackedCategoryNames,
    trackedCategoryIds,
  } = useWhatBringsYouVariantPALM()

  const { firstQuizQuestionv2 } = useHPHeadingsExp() // pen-4680_v1
  const { title: quizOutcomeFramingTitle } = useQuizOutcomeFramingExp() // pen-5819_v1

  return (
    <div className='row no-gutters'>
      <div className='col-12 col-lg-10'>
        <div className='mc-text-h5'>
          {quizOutcomeFramingTitle ||
            firstQuizQuestionv2 ||
            t('home.hero.quiz.title')}
        </div>
        <div className='mc-my-4'>
          <Questions
            sel={sel}
            setSel={setSel}
            toggleCategory={toggle}
            isCategorySelected={isSelected}
          />
        </div>
        <div className='d-flex flex-wrap mc-mb-4 mc-mb-md-0'>
          <TooltipCta
            disabled={selectedOptions.length === 0}
            href={ctaHref}
            action='Continue'
            onClick={() => {
              trackPrimerAnswerSubmitted({
                selectedOptions,
                selectedCategories: trackedCategoryNames,
                selectedCategoryIds: trackedCategoryIds,
                stepName: 'primer-question-modified',
              })
            }}
          >
            {t('home.hero.cta.continue')}
          </TooltipCta>
        </div>
      </div>
    </div>
  )
}

export type Sel = Map<Motivation, boolean>
type SetSel = (f: (s: Sel) => Sel) => void
type QuestionsProps = {
  sel: Sel
  setSel: SetSel
  toggleCategory(categoryId: Category['id']): void
  isCategorySelected(categoryId: Category['id']): boolean
  otherIsPreselected?: boolean
  location?: typeof Locations.HERO | typeof Locations.QUIZ_MODULE
}

export const Questions = ({
  sel,
  setSel,
  toggleCategory,
  isCategorySelected,
  otherIsPreselected = false,
  location = Locations.HERO,
}: QuestionsProps) => {
  const { t } = useTranslation('@mc/next')
  const catNameToId = useCategoryNameToIdMap()
  const motivations = useMotivations(otherIsPreselected)
  const motivationCopyMap = useMotivationsCopyMap(motivations, t)
  const motivationToCategoriesMap =
    useMotivationToCategoriesMap(otherIsPreselected)

  const isOptionSelected = (motivationName: Motivation) =>
    !!sel.get(motivationName)

  const toggle = (m: Motivation) => {
    const value = motivationCopyMap[m]
    const checked = isOptionSelected(m)
    setSel((oldMap) => {
      const selections = new Map(oldMap)
      return selections.set(m, !selections.get(m))
    })

    const mappedCategories = motivationToCategoriesMap[m] || []
    mappedCategories.forEach((categoryName) => {
      const catId = catNameToId.get(categoryName)
      if (catId && isCategorySelected(catId) === checked) {
        const mappedCatId = Number(catNameToId.get(categoryName))
        toggleCategory(mappedCatId)
      }
    })

    trackCheckboxToggled({
      value,
      checked,
      stepName: 'primer-question-modified',
      location,
    })
  }

  return (
    <div className='mc-corners--sm mc-overflow--hidden'>
      {motivations.map((motivation, index) => (
        <div
          key={motivation}
          className={cn({ 'mc-mb-1': index !== motivations.length - 1 })}
        >
          <CheckboxSelectionTight
            checked={isOptionSelected(motivation)}
            onClick={() => toggle(motivation)}
          >
            {motivationCopyMap[motivation]}
          </CheckboxSelectionTight>
        </div>
      ))}
    </div>
  )
}
